import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { AuthContext } from "./AuthContext";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const auth = useContext(AuthContext);
  const isAuth = auth ? auth.isAuth : false;

  const getActiveUserId = () => localStorage.getItem("userId") || "guest";
  const [activeUserId, setActiveUserId] = useState(getActiveUserId);

  useEffect(() => {
    const checkUser = () => setActiveUserId(getActiveUserId());
    checkUser();
    window.addEventListener("storage", checkUser);
    return () => window.removeEventListener("storage", checkUser);
  }, [isAuth]);

  const getCartKey = (uid) => `wellness_shop_cart_${uid}`;

  const [cart, setCart] = useState(() => {
    try {
      const activeKey = getCartKey(getActiveUserId());
      const saved = localStorage.getItem(activeKey);
      if (saved && JSON.parse(saved).length > 0) {
        return JSON.parse(saved);
      }
      const legacy = localStorage.getItem("wellness_shop_cart");
      if (legacy && JSON.parse(legacy).length > 0) {
        const legacyItems = JSON.parse(legacy);
        localStorage.setItem(activeKey, JSON.stringify(legacyItems));
        localStorage.removeItem("wellness_shop_cart");
        return legacyItems;
      }
      return [];
    } catch {
      return [];
    }
  });

  // Re-load user's cart when user changes
  useEffect(() => {
    try {
      const activeKey = getCartKey(activeUserId);
      const saved = localStorage.getItem(activeKey);
      if (saved && JSON.parse(saved).length > 0) {
        setCart(JSON.parse(saved));
      } else {
        const legacy = localStorage.getItem("wellness_shop_cart");
        if (legacy && JSON.parse(legacy).length > 0) {
          const legacyItems = JSON.parse(legacy);
          localStorage.setItem(activeKey, JSON.stringify(legacyItems));
          localStorage.removeItem("wellness_shop_cart");
          setCart(legacyItems);
        } else {
          setCart([]);
        }
      }
    } catch {
      setCart([]);
    }
  }, [activeUserId]);

  // Persist to user-specific localStorage key
  useEffect(() => {
    localStorage.setItem(getCartKey(activeUserId), JSON.stringify(cart));
  }, [cart, activeUserId]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item._id === product._id);
      if (existing) {
        return prev.map((item) =>
          item._id === product._id
            ? { ...item, qty: Math.min(item.qty + qty, product.stockQty) }
            : item
        );
      }
      return [...prev, { ...product, qty: Math.min(qty, product.stockQty) }];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((item) => item._id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, newQty) => {
    if (newQty < 1) {
      setCart((prev) => prev.filter((item) => item._id !== productId));
    } else {
      setCart((prev) =>
        prev.map((item) =>
          item._id === productId ? { ...item, qty: newQty } : item
        )
      );
    }
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  return (
    <CartContext.Provider
      value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};

export default CartProvider;
